import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { ApiIssue } from "../api/errors";
import { productsApi } from "../api/products";
import type { ColorVariant, Revision } from "../api/types";
import { LENS_COLORS, lensColorForKey } from "../catalog/lensColors";
import {
  SIZE_LABELS,
  SIZE_OPTIONS,
  isSizeOption,
  type ProductFamily,
  type SizeOption,
} from "../catalog/taxonomy";
import { ErrorNotice, Panel } from "../components/ui";
import { ConfirmDialog } from "./ConfirmDialog";
import { issuesByField, useProductRefresh } from "./useProduct";

/** Stable empty map, so a row with nothing wrong does not get a new object on
 *  every render. */
const NO_ISSUES: Map<string, ApiIssue[]> = new Map();

/**
 * The way from a product to the shelf it sits on.
 *
 * Stock is READ-ONLY everywhere on this screen. It used to be an editable box
 * that wrote an absolute number on blur — no lock, no movement, no reason, no
 * idempotency — so a figure typed against a five-minute-old page silently
 * overwrote whatever had sold since, and nothing recorded that it had. Moving
 * stock happens in Inventory, which locks the item, compares what the operator
 * was looking at with what is really there, and writes a movement saying why.
 *
 * The Adjust dialog is deliberately NOT duplicated here. Two ways to move
 * stock is how the two come to disagree about what the rules are; this is a
 * link to the one that exists, scoped to the product being looked at so the
 * operator lands on its rows rather than on the whole shop.
 */
function ManageInventoryLink({ productName }: { productName: string }) {
  return (
    <Link
      className="btn btn--sm"
      to={`/inventory?search=${encodeURIComponent(productName)}`}
    >
      Manage inventory
    </Link>
  );
}

/** Stock as a fact rather than a field. */
function StockReadout({ variant }: { variant: ColorVariant }) {
  return (
    <span className="num" aria-label={`Stock for ${variant.name}`}>
      {variant.stock_quantity}
    </span>
  );
}

/**
 * The colours of a product, in whatever way its family actually has them.
 *
 * A frame is a table: one editable row per colour, because adjusting stock
 * across six of them should not mean opening six dialogs.
 *
 * A cosmetic lens is not a table. One lens product is ONE colour — Ash Grey and
 * Hazel are two products, not two settings of one — and it must be one of the
 * six the phone's filter can find, so it is a single choice from those six
 * rather than a free-text name and a hex box that can be typed wrong and only
 * refused at publish.
 *
 * An accessory that comes one way is not a table either. Its single variant is
 * a structural fact the backend keeps stock on, not a decision anyone made, and
 * showing it as a colour row called "Default" would invent a choice the shop
 * does not offer. So it is shown as stock, and becomes a table only if it
 * genuinely has more than one variant.
 */
export function VariantsPanel({
  productId,
  draft,
  editable,
  family,
}: {
  productId: string;
  draft: Revision;
  editable: boolean;
  family: ProductFamily | null;
}) {
  const refresh = useProductRefresh(productId);
  const [error, setError] = useState<unknown>(null);
  // Which colour the failure belongs to, so a rejected edit is reported on the
  // row that caused it rather than as an anonymous banner over the table.
  const [failedVariantId, setFailedVariantId] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<ColorVariant | null>(null);
  const [newName, setNewName] = useState("");

  const variants = [...(draft.color_variants ?? [])].sort(
    (a, b) => a.display_order - b.display_order,
  );

  // Every failure reloads: a stale version needs a fresh token before the next
  // attempt, and any other failure may have left the draft in a state the form
  // no longer reflects. Nothing is ever resent automatically.
  const update = useMutation({
    mutationFn: (input: { variantId: string; changes: Record<string, unknown> }) =>
      productsApi.updateVariant(productId, input.variantId, {
        expected_version: draft.version,
        ...input.changes,
      }),
    onSuccess: async () => {
      setError(null);
      setFailedVariantId(null);
      await refresh();
    },
    onError: async (caught, input) => {
      setError(caught);
      setFailedVariantId(input.variantId);
      await refresh();
    },
  });

  const add = useMutation({
    mutationFn: (input: { name: string } & Record<string, unknown>) =>
      productsApi.addVariant(productId, {
        expected_version: draft.version,
        ...input,
      }),
    onSuccess: async () => {
      setError(null);
      setFailedVariantId(null);
      setNewName("");
      await refresh();
    },
    onError: async (caught) => {
      setError(caught);
      // Adding a colour belongs to no existing row.
      setFailedVariantId(null);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (variantId: string) =>
      productsApi.removeVariant(productId, variantId, draft.version),
    onSuccess: async () => {
      setError(null);
      setFailedVariantId(null);
      setPendingRemoval(null);
      await refresh();
    },
    onError: async (caught) => {
      setError(caught);
      setFailedVariantId(null);
      setPendingRemoval(null);
      await refresh();
    },
  });

  const busy = update.isPending || add.isPending || remove.isPending;

  // The backend keys each issue to the field it concerns. Where it named one,
  // the message belongs beside that input — a summary banner saying "the colour
  // could not be updated" tells an administrator nothing they can act on. The
  // banner is kept only for failures that name no field: a stale draft, a
  // colour that no longer exists, a failed add or remove.
  const fieldIssues = failedVariantId ? issuesByField(error) : NO_ISSUES;
  const hasFieldIssue = [...fieldIssues.keys()].some((field) => field !== "");

  const only = variants.length === 1 ? variants[0]! : null;

  // A lens: the one approved colour, as a choice from the six. Handles the
  // not-yet-chosen case too — there is no free-text "add a colour" box on this
  // path at all, which is what makes a second enabled lens colour and an
  // unapproved key both unexpressible rather than merely refused later.
  if (family === "contact_lenses" && variants.length <= 1) {
    return (
      <Panel
        title="Lens colour"
        actions={<ManageInventoryLink productName={draft.name} />}
      >
        {error ? <ErrorNotice error={error} /> : null}
        <LensColorPanel
          variant={only}
          editable={editable && !busy}
          onChoose={(color) => {
            if (only) {
              update.mutate({
                variantId: only.id,
                changes: { name: color.name, swatch_hex: color.swatch_hex },
              });
            } else {
              add.mutate({ name: color.name, swatch_hex: color.swatch_hex });
            }
          }}
        />
      </Panel>
    );
  }

  // An accessory that comes one way: stock, with no invented colour.
  if (family === "accessories" && only) {
    return (
      <Panel
        title="Stock"
        actions={<ManageInventoryLink productName={draft.name} />}
      >
        {error ? <ErrorNotice error={error} /> : null}
        <StructuralStockPanel
          variant={only}
          editable={editable && !busy}
          onAddVariant={(name) => add.mutate({ name })}
        />
      </Panel>
    );
  }

  return (
    <Panel
      title={
        <>
          Colors <span className="workspace__tabCount">{variants.length}</span>
        </>
      }
      actions={<ManageInventoryLink productName={draft.name} />}
      bodyClass=""
    >
      {error && !hasFieldIssue ? (
        <div className="panel__body" style={{ paddingBottom: 0 }}>
          <ErrorNotice error={error} />
        </div>
      ) : null}

      {variants.length === 0 ? (
        <div className="empty">This draft has no colors yet.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Name</th>
              <th>Key</th>
              <th style={{ width: 110 }}>Swatch</th>
              <th style={{ width: 90 }}>Stock</th>
              {/* A FRAME's size belongs to the product entry, not to each
                  colour of it: one entry is one size, and another size is
                  another product with its own code and its own stock. The
                  other families never had a frame size to begin with, and
                  their column is left exactly as it was. */}
              {family === "glasses" ? null : (
                <th style={{ width: "18%" }}>Sizes</th>
              )}
              <th style={{ width: 80 }}>Order</th>
              <th style={{ width: 90 }}>Shown</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {variants.map((variant) => (
              <VariantRow
                key={variant.id}
                variant={variant}
                editable={editable && !busy}
                showSizes={family !== "glasses"}
                issues={variant.id === failedVariantId ? fieldIssues : NO_ISSUES}
                onChange={(changes) =>
                  update.mutate({ variantId: variant.id, changes })
                }
                onRemove={() => setPendingRemoval(variant)}
              />
            ))}
          </tbody>
        </table>
      )}

      {editable ? (
        <div className="panel__body row">
          <label className="sr-only" htmlFor="new-variant">
            New color name
          </label>
          <input
            id="new-variant"
            placeholder="Add a color, e.g. Tortoise"
            value={newName}
            style={{ width: 260 }}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim()) {
                e.preventDefault();
                add.mutate({ name: newName.trim() });
              }
            }}
            disabled={busy}
          />
          <button
            type="button"
            className="btn btn--sm"
            disabled={!newName.trim() || busy}
            onClick={() => add.mutate({ name: newName.trim() })}
          >
            Add color
          </button>
        </div>
      ) : null}

      {pendingRemoval ? (
        <ConfirmDialog
          title={`Remove ${pendingRemoval.name} from this draft?`}
          confirmLabel="Remove from draft"
          destructive
          busy={remove.isPending}
          onCancel={() => setPendingRemoval(null)}
          onConfirm={() => remove.mutate(pendingRemoval.id)}
        >
          This color is removed from the draft you are editing. Published
          revisions keep their own copy, and no stored image is deleted.
        </ConfirmDialog>
      ) : null}
    </Panel>
  );
}

/**
 * The one colour a cosmetic lens is sold in.
 *
 * Six choices and no text box. The backend refuses an unapproved key at publish
 * (APPROVED_LENS_COLOR_KEYS) because the phone's lens filter owns a fixed
 * vocabulary and a lens outside it would be invisible under every chip — so a
 * free-text name here would be a field whose wrong answers are only discovered
 * at the end. There is also no "add another": a second colour is a second
 * product, and this screen offers no way to express one.
 */
function LensColorPanel({
  variant,
  editable,
  onChoose,
}: {
  variant: ColorVariant | null;
  editable: boolean;
  onChoose: (color: { name: string; swatch_hex: string }) => void;
}) {
  // Matched by the stored key, which is what the phone filters on. A lens
  // carrying something else is reported rather than quietly shown as approved.
  const current = lensColorForKey(variant?.normalized_color_key);

  return (
    <div className="stack" data-testid="lens-color-panel">
      <fieldset className="field" disabled={!editable}>
        <legend className="field__label">Lens colour</legend>
        <p className="field__hint">
          One colour per product. Ash Grey and Hazel are two products, not two
          settings of one.
        </p>
        <div className="choiceRow">
          {LENS_COLORS.map((color) => (
            <label
              key={color.key}
              className={current?.key === color.key ? "choice is-selected" : "choice"}
            >
              <input
                type="radio"
                name="lens-color"
                value={color.key}
                checked={current?.key === color.key}
                disabled={!editable}
                onChange={() =>
                  onChoose({ name: color.name, swatch_hex: color.swatch_hex })
                }
              />
              <span
                className="swatch"
                style={{ background: color.swatch_hex }}
                aria-hidden="true"
              />
              <span className="choice__label">{color.name}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {variant && !current ? (
        <div className="notice notice--warning" data-testid="lens-color-unapproved">
          This lens is stored as{" "}
          <span className="mono">{variant.normalized_color_key}</span>, which is
          not one of the six colours customers can filter by. Choose one above
          before publishing.
        </div>
      ) : null}

      {variant ? (
        <div className="field">
          <span className="field__label">Stock</span>
          <p>
            <StockReadout variant={variant} /> in stock.{" "}
            <span className="muted">
              Changed through Inventory, which records why.
            </span>
          </p>
        </div>
      ) : (
        <p className="muted">Choose a colour before stocking this lens.</p>
      )}
    </div>
  );
}

/**
 * An accessory that comes one way: its stock, and nothing pretending to be a
 * colour.
 *
 * The backend keeps stock on a variant, so one exists — but that is a
 * structural fact about the schema, not a choice a customer is offered. Showing
 * it as a colour row named "Default" would put an invented option in front of
 * an administrator as if naming it were their job, and then in front of a
 * shopper as if choosing it were theirs.
 */
function StructuralStockPanel({
  variant,
  editable,
  onAddVariant,
}: {
  variant: ColorVariant;
  editable: boolean;
  onAddVariant: (name: string) => void;
}) {
  const [name, setName] = useState("");

  return (
    <div className="stack" data-testid="structural-stock-panel">
      <p className="muted">
        This accessory comes one way, so customers are not asked to choose a
        variant.
      </p>
      <div className="field">
        <span className="field__label">Stock</span>
        <p>
          <StockReadout variant={variant} /> in stock.{" "}
          <span className="muted">
            Changed through Inventory, which records why.
          </span>
        </p>
      </div>

      {/* If it turns out to have a real choice, saying so here turns this into
          the ordinary variant table — the same one every other multi-variant
          product uses, rather than a second editor for accessories. */}
      {editable ? (
        <div className="row">
          <label className="sr-only" htmlFor="accessory-new-variant">
            New variant name
          </label>
          <input
            id="accessory-new-variant"
            placeholder="Add a variant, e.g. Large"
            style={{ width: 260 }}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            type="button"
            className="btn btn--sm"
            disabled={!name.trim()}
            onClick={() => {
              onAddVariant(name.trim());
              setName("");
            }}
          >
            Add variant
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Edits commit on blur rather than on each keystroke: every save costs a
 *  version bump, and a per-character mutation would make the draft version race
 *  against the administrator's own typing. */
function VariantRow({
  variant,
  editable,
  showSizes,
  issues,
  onChange,
  onRemove,
}: {
  variant: ColorVariant;
  editable: boolean;
  /** False for frames: their size is the product entry's, not the colour's. */
  showSizes: boolean;
  issues: Map<string, ApiIssue[]>;
  onChange: (changes: Record<string, unknown>) => void;
  onRemove: () => void;
}) {
  function commit(key: string, value: unknown, previous: unknown) {
    if (value === previous) return;
    onChange({ [key]: value });
  }

  return (
    <tr className={variant.enabled ? undefined : "is-disabled-row"}>
      <td>
        <input
          className="variant-input variant-input--name"
          defaultValue={variant.name}
          aria-label={`Name for ${variant.name}`}
          aria-invalid={issues.has("name")}
          disabled={!editable}
          onBlur={(e) => commit("name", e.target.value.trim(), variant.name)}
        />
        <IssueText issues={issues.get("name")} />
      </td>
      <td className="mono">{variant.normalized_color_key}</td>
      <td>
        <div className="row" style={{ gap: "var(--s2)" }}>
          <span className="swatch" style={{ background: variant.swatch_hex }} />
          <input
            className="variant-input variant-input--hex"
            defaultValue={variant.swatch_hex}
            aria-label={`Swatch for ${variant.name}`}
            aria-invalid={issues.has("swatch_hex")}
            disabled={!editable}
            onBlur={(e) =>
              commit("swatch_hex", e.target.value.trim(), variant.swatch_hex)
            }
          />
        </div>
        <IssueText issues={issues.get("swatch_hex")} />
      </td>
      {/* Read-only. This was an editable box that wrote an absolute quantity on
          blur, which is how a stale screen silently overwrote a sale. */}
      <td className="num">
        <StockReadout variant={variant} />
      </td>
      {showSizes ? (
        <td>
          <SizeSelect
            variant={variant}
            editable={editable}
            invalid={issues.has("size_options")}
            onChange={(size_options) => onChange({ size_options })}
          />
          <IssueText issues={issues.get("size_options")} />
        </td>
      ) : null}
      <td>
        <input
          className="variant-input variant-input--num num"
          type="number"
          defaultValue={variant.display_order}
          aria-label={`Order for ${variant.name}`}
          aria-invalid={issues.has("display_order")}
          disabled={!editable}
          onBlur={(e) =>
            commit(
              "display_order",
              Number.parseInt(e.target.value || "0", 10),
              variant.display_order,
            )
          }
        />
        <IssueText issues={issues.get("display_order")} />
      </td>
      <td>
        <label className="row" style={{ gap: "var(--s1)", marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={variant.enabled}
            aria-label={`Show ${variant.name}`}
            disabled={!editable}
            onChange={(e) => onChange({ enabled: e.target.checked })}
          />
          <span className="muted">{variant.enabled ? "Shown" : "Hidden"}</span>
        </label>
      </td>
      <td>
        <button
          type="button"
          className="btn btn--subtle btn--sm"
          disabled={!editable}
          onClick={onRemove}
        >
          Remove
        </button>
      </td>
    </tr>
  );
}

/**
 * The sizes this colour is sold in: the shop's three, as three checkboxes.
 *
 * It replaces a free-text box that accepted "S, M, S" and any word an
 * administrator cared to type. Two things follow from the control rather than
 * from validation, which is the point of changing it:
 *
 *  - A duplicate cannot be expressed. The value sent is built by filtering
 *    SIZE_OPTIONS, so each size appears at most once no matter what is clicked,
 *    and the backend's sizes_duplicated rejection becomes unreachable from this
 *    screen instead of something the operator has to be taught to avoid.
 *  - A size outside the shop's three cannot be expressed either, so a variant
 *    can never carry a size the phone has no segment for.
 *
 * Order is SIZE_OPTIONS order, not click order: S, M, L reads as a size run,
 * and "L, S" would be the same product described worse.
 */
function SizeSelect({
  variant,
  editable,
  invalid,
  onChange,
}: {
  variant: ColorVariant;
  editable: boolean;
  invalid: boolean;
  onChange: (sizes: SizeOption[]) => void;
}) {
  const selected = new Set((variant.size_options ?? []).filter(isSizeOption));

  return (
    <fieldset
      className="sizeSelect"
      aria-label={`Sizes for ${variant.name}`}
      aria-invalid={invalid}
      disabled={!editable}
    >
      {SIZE_OPTIONS.map((size) => (
        <label key={size} className="sizeSelect__option">
          <input
            type="checkbox"
            checked={selected.has(size)}
            aria-label={`${SIZE_LABELS[size]} for ${variant.name}`}
            disabled={!editable}
            onChange={(event) => {
              const next = new Set(selected);
              if (event.target.checked) next.add(size);
              else next.delete(size);
              // Rebuilt from the canonical list: unique by construction, and
              // always in the shop's own order.
              onChange(SIZE_OPTIONS.filter((option) => next.has(option)));
            }}
          />
          <span>{size}</span>
        </label>
      ))}
    </fieldset>
  );
}

/** The backend's own words for what is wrong with one field, under that field.
 *
 * Same presentation as the metadata form's `Field`, so a rejected colour and a
 * rejected price look like the same kind of problem to the person fixing them. */
function IssueText({ issues }: { issues?: ApiIssue[] }) {
  if (!issues?.length) return null;
  return (
    <>
      {issues.map((issue) => (
        <p className="field-error" key={`${issue.code}-${issue.message}`}>
          {issue.message}
        </p>
      ))}
    </>
  );
}
